<template>
  <div class="item-master-section fw fh">
    <div class="item-master-section__card">
      <div class="item-master-section__title">
        <img class="item-master-section__icon" width="12" height="12"
         :src="sectionIcon" alt="Basic info" />
        <span>Basic Information</span>
      </div>
      <div class="item-master-section__grid">
        <div class="item-master-section__field">
          <span class="label">{{ viewLabel }} Code</span>
          <span class="value">{{ materialMaster.custom_material_id || '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Product Code</span>
          <span class="value">{{ materialMaster.material_product_code?.product_code || '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">ERP Material Id</span>
          <span class="value">{{ materialMaster.erp_material_id || '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Material Group</span>
          <span class="value">{{ materialMaster.material_group_details?.name || '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Unit of Material</span>
          <span class="value">{{ materialMaster.material_unit_details?.name || '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Type</span>
          <span class="value">{{ typeLabel }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Dimensions</span>
          <span class="value">{{ dimensionLabel }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Volume</span>
          <span class="value">{{ volumeLabel }} {{ materialMaster?.unit_of_volume || '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">PLM Record Id</span>
          <span class="value">{{ materialMaster.plm_record_id || '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Weight</span>
          <span class="value">{{ grossWeightLabel }} {{ materialMaster.material_weight_details?.name || '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Effective Date</span>
          <span class="value">{{ materialMaster.effective_date || '--' }}</span>
        </div>
      </div>
    </div>

    <div class="item-master-section__card">
      <div class="item-master-section__title">
        <img class="item-master-section__icon" :src="manufacturingIcon" alt="Manufacturing" />
        <span>Manufacturing Details</span>
      </div>
      <div class="item-master-section__grid">
        <div class="item-master-section__field">
          <span class="label">Process Plan</span>
          <span class="value">{{ materialMaster.workflow_template?.name || '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Manufacturing Time</span>
          <span class="value">{{ leadTimeLabel }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Default Vendor</span>
          <span class="value">{{ materialMaster.material_default_vendor?.company_name || '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Storage Loc</span>
          <span class="value">{{ materialMaster.material_storage_location?.name || '--' }}</span>
        </div>
      </div>
    </div>

    <div class="item-master-section__card">
      <div class="item-master-section__title">
        <img class="item-master-section__icon" :src="inventoryIcon" alt="Inventory" />
        <span>Inventory Information</span>
      </div>
      <div class="item-master-section__grid">
        <div class="item-master-section__field">
          <span class="label">Committed Inventory</span>
          <span class="value">{{ materialMaster.inventory ?? '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Total Inventory</span>
          <span class="value">{{ materialMaster.inventory ?? '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Unit Cost</span>
          <span class="value">{{ materialMaster.unit_cost ?? '--' }}</span>
        </div>
        <div class="item-master-section__field">
          <span class="label">Unit Sale Price</span>
          <span class="value">{{ materialMaster.unit_sale_price ?? '--' }}</span>
        </div>
      </div>
    </div>

    <div class="item-master-section__card">
      <div class="item-master-section__title item-master-section__title--between">
        <div class="item-master-section__title-left">
          <img class="item-master-section__icon" :src="bomIcon" alt="BOM usage" />
          <span>BOM Usage</span>
        </div>
        <span class="item-master-section__chip">
          {{ bomUsageChipLabel }}
        </span>
      </div>

      <div class="item-master-section__bom-list">
        <skeleton v-if="bomUsageLoading" :rows="3" />
        <template v-else>
          <div v-for="(item, index) in bomUsageItems" :key="index" class="item-master-section__bom-item">
            <div class="item-master-section__bom-left">
              <div class="item-master-section__bom-icon"><img width="15" src="@/assets/images/icons/bom-layer-icon.svg" alt=""></div>
              <div class="item-master-section__bom-text">
                <div class="item-master-section__bom-name elipsis-text" v-overflow-tooltip="item.name">
                  {{ item.name }}
                  <span v-if="item.badge" class="item-master-section__badge">{{ item.badge }}</span>
                </div>
                <div v-if="item.code" class="item-master-section__bom-meta">
                  {{ item.code }} • in {{ item.assembly }}
                </div>
              </div>
            </div>
            <div class="item-master-section__bom-right">
              <div class="item-master-section__bom-sub"> Quantity : {{ item.quantity }} </div>
              <div class="item-master-section__bom-sub"> Unit Size : {{ item.unit_size }} </div>
            </div>
          </div>
          <div v-if="!bomUsageItems.length" class="item-master-section__empty">
            No BOM usage
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { alert } from '@/plugins/notification'
import { GetProductsUsingMaterialQuery } from '@/api/apis/materialMaster'
import Skeleton from '@/components/common/skeleton.vue'

export default {
  name: 'ItemMasterDetailsSection',
  components: { Skeleton },
  props: {
    materialMaster: {
      type: Object,
      default: () => ({})
    },
    view: {
      type: String,
      default: 'material'
    }
  },
  data () {
    return {
      bomUsageItems: [],
      bomUsageLoading: false
    }
  },
  computed: {
    ...mapGetters(['isOnProjectLevel']),
    viewLabel () {
      return this.view === 'resource' ? 'Resource' : 'Material'
    },
    typeLabel () {
      if (this.view === 'resource') {
        return this.materialMaster.resource_type_value?.name || '--'
      }
      return this.materialMaster.material_type_value?.name || '--'
    },
    leadTimeLabel () {
      if (this.materialMaster.lead_time) {
        return `${this.materialMaster.lead_time} days`
      }
      return '--'
    },
    volumeLabel () {
      if (this.materialMaster.volume === null || this.materialMaster.volume === undefined || this.materialMaster.volume === '') {
        return '--'
      }
      return `${this.materialMaster.volume}`
    },
    dimensionLabel () {
      if (this.materialMaster.dimension === null || this.materialMaster.dimension === undefined || this.materialMaster.dimension === '') {
        return '--'
      }
      return this.materialMaster.dimension
    },
    grossWeightLabel () {
      if (this.materialMaster.gross_weight === null || this.materialMaster.gross_weight === undefined || this.materialMaster.gross_weight === '') {
        return '--'
      }
      return this.materialMaster.gross_weight
    },
    sectionIcon () {
      return require('@/assets/images/icons/list-icon.svg')
    },
    manufacturingIcon () {
      return require('@/assets/images/icons/workflow-icon.svg')
    },
    inventoryIcon () {
      return require('@/assets/images/icons/forecast.svg')
    },
    bomIcon () {
      return require('@/assets/images/icons/check-list-icon.svg')
    },
    bomUsageChipLabel () {
      if (this.bomUsageLoading) {
        return 'Loading...'
      }
      return `Used in ${this.bomUsageItems.length} BOMs`
    }
  },
  mounted () {
    this.fetchBomUsage()
  },
  methods: {
    fetchBomUsage () {
      const materialId = this.materialMaster?.id
      if (!materialId) {
        this.bomUsageItems = []
        return
      }

      this.bomUsageLoading = true
      GetProductsUsingMaterialQuery(materialId).then((res) => {
        const items = []
        const bomItems = res?.core_material_master?.[0]?.bom_material_items || []
        if (this.isOnProjectLevel) {
          bomItems.forEach((item) => {
            items.push({
              name: item.core_bom?.name || '--',
              quantity: item.quantity,
              unit_size: item.unit_size
            })
          })
        } else {
          bomItems.forEach((item) => {
            const productCode = item.core_bom?.product_bom?.product_code
            items.push({
              name: item.core_bom?.name || '--',
              code: productCode,
              assembly: productCode,
              quantity: item.quantity,
              unit_size: item.unit_size
            })
          })
        }
        this.bomUsageItems = items
      }).catch(() => {
        alert('Failed to fetch data')
      }).finally(() => {
        this.bomUsageLoading = false
      })
    }
  },
  watch: {
    'materialMaster.id' () {
      this.fetchBomUsage()
    }
  }
}
</script>

<style lang="scss" scoped>
.item-master-section {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 32px 8px 32px;

  &__card {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 20px 22px;
    background: #ffffff;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 400;
    font-size: var(--text-s);
    color: #0f172a;
    margin-bottom: 16px;
  }
  &__title--between {
    justify-content: space-between;
  }
  &__title-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__icon {
    width: 14px;
    height: 14px;
    filter: invert(54%) sepia(48%) saturate(531%) hue-rotate(350deg) brightness(90%) contrast(90%);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 24px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .label {
    color: #8a8e97;
    font-size: var(--text-xs2);
    font-weight: 500;
  }

  .value {
    color: #0f172a;
    font-weight: 400;
    font-size: var(--text-xs2);
  }

  &__chip {
    font-size: var(--text-xs2);
    font-weight: 500;
    color: #0f172a;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    padding: 4px 10px;
    border-radius: 999px;
  }

  &__bom-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__bom-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fafafa;
  }

  &__bom-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  &__bom-icon {
    min-width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #ede9fe;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__bom-text {
    min-width: 0;
  }

  &__bom-name {
    font-size: var(--text-xs2);
    font-weight: 500;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__bom-meta {
    font-size: var(--text-xs2);
    color: #6b7280;
  }

  &__badge {
    font-size: 10px;
    font-weight: 500;
    color: #1d4ed8;
    background: #dbeafe;
    padding: 2px 6px;
    border-radius: 6px;
  }

  &__bom-right {
    text-align: right;
    min-width: 90px;
  }

  &__bom-qty {
    font-size: var(--text-xs2);
    font-weight: 600;
    color: #0f172a;
  }

  &__bom-sub {
    font-size: 11px;
    color: #6b7280;
  }

  &__empty {
    font-size: var(--text-xs2);
    color: #9ca3af;
    padding: 6px 2px;
  }
}

@media (max-width: 768px) {
  .item-master-section {
    padding: 20px;

    &__grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
